/**
 * Client for the Dridh Agent API (/api/agent). REST goes through the shared
 * axios instance (token, kill-switch handling); the live event stream uses
 * fetch because EventSource cannot send the Authorization header.
 */
import api from '../services/api';
import API_CONFIG from '../config/api';
import { CLIENT_APP_HEADER, announceServicePaused } from '../services/systemStatusService';

function toError(error) {
  const data = error?.response?.data;
  const message = data?.error || error?.errorMessage || error?.message || 'Request failed';
  const wrapped = new Error(typeof message === 'string' ? message : 'Request failed');
  wrapped.status = error?.response?.status;
  wrapped.data = data;
  wrapped.issues = data?.issues;
  return wrapped;
}

async function call(request) {
  try {
    return await request;
  } catch (error) {
    throw toError(error);
  }
}

const LONG = { timeout: 120000 };

/** Hands a downloaded file to the browser (the download needs the auth header, so no plain link). */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export const agentApi = {
  meta: () => call(api.get('/api/agent/meta')),
  stats: (scope) => call(api.get('/api/agent/stats', { params: scope ? { scope } : {} })),

  listSessions: (archived = false) => call(api.get('/api/agent/sessions', { params: { archived } })),
  createSession: (body) => call(api.post('/api/agent/sessions', body)),
  getSession: (id) => call(api.get(`/api/agent/sessions/${id}`)),
  updateSession: (id, body) => call(api.patch(`/api/agent/sessions/${id}`, body)),
  sendMessage: (id, body) => call(api.post(`/api/agent/sessions/${id}/messages`, body)),
  getEvent: (id, seq) => call(api.get(`/api/agent/sessions/${id}/events/${seq}`)),
  verify: (id) => call(api.get(`/api/agent/sessions/${id}/verify`)),

  decide: (runId, approve, note) => call(api.post(`/api/agent/runs/${runId}/decisions`, { approve, note })),
  cancel: (runId) => call(api.post(`/api/agent/runs/${runId}/cancel`)),
  undoRun: (runId, force = false) => call(api.post(`/api/agent/runs/${runId}/undo`, { force })),
  undoAction: (actionId, force = false) => call(api.post(`/api/agent/actions/${actionId}/undo`, { force })),
  listActions: (params) => call(api.get('/api/agent/actions', { params })),

  listSkills: (includeArchived = false) => call(api.get('/api/agent/skills', { params: { includeArchived } })),
  getSkill: (slug) => call(api.get(`/api/agent/skills/${slug}`)),
  createSkill: (body) => call(api.post('/api/agent/skills', body)),
  updateSkill: (slug, body) => call(api.put(`/api/agent/skills/${slug}`, body)),
  archiveSkill: (slug) => call(api.post(`/api/agent/skills/${slug}/archive`)),
  restoreSkill: (slug, version) => call(api.post(`/api/agent/skills/${slug}/restore`, { version })),

  listProfiles: () => call(api.get('/api/agent/profiles')),
  createProfile: (body) => call(api.post('/api/agent/profiles', body)),
  updateProfile: (id, body) => call(api.patch(`/api/agent/profiles/${id}`, body)),
  deleteProfile: (id) => call(api.delete(`/api/agent/profiles/${id}`)),
  testProfile: (id) => call(api.post(`/api/agent/profiles/${id}/test`, {}, LONG)),
  testUnsavedProfile: (body) => call(api.post('/api/agent/profiles/test', body, LONG)),

  getSettings: () => call(api.get('/api/agent/settings')),
  updateSettings: (body) => call(api.put('/api/agent/settings', body)),

  listFiles: (sessionId) => call(api.get('/api/agent/files', { params: sessionId ? { sessionId } : {} })),
  downloadFile: async (fileId, filename) => {
    const blob = await call(api.get(`/api/agent/files/${fileId}/download`, { responseType: 'blob', ...LONG }));
    saveBlob(blob, filename);
  },
};

/**
 * Reads server-sent events until the server asks for a reconnect, the stream
 * ends, or `signal` aborts. Resolves normally in the first two cases.
 */
export async function streamSession(sessionId, { after, onEvent, onState, signal }) {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_CONFIG.BASE_URL}/api/agent/sessions/${sessionId}/stream?after=${after || 0}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-App': CLIENT_APP_HEADER,
      Accept: 'text/event-stream',
    },
    signal,
  });

  if (response.status === 503) {
    try {
      const data = await response.json();
      if (data?.code === 'SERVICE_PAUSED') announceServicePaused(data.maintenance);
    } catch (error) {
      // not a kill-switch payload
    }
    throw Object.assign(new Error('Service paused'), { status: 503 });
  }
  if (!response.ok || !response.body) {
    throw Object.assign(new Error(`Live updates unavailable (${response.status})`), { status: response.status });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');
      const name = /^event: (.+)$/m.exec(frame)?.[1];
      const data = /^data: (.+)$/m.exec(frame)?.[1];
      if (!name || !data) continue;
      let parsed;
      try {
        parsed = JSON.parse(data);
      } catch (error) {
        continue;
      }
      if (name === 'ledger') onEvent?.(parsed);
      else if (name === 'state') onState?.(parsed);
      else if (name === 'reconnect') return;
    }
  }
}
